import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  Eye,
  Plus,
  Users,
  Edit,
  PoundSterling,
  Calendar,
  Trash2,
  MoreVertical,
  CheckCircle,
  XCircle,
  FileText,
  ChevronDown,
  EyeClosedIcon,
  ScreenShare,
  Users2,
  UserCheck,
  FileUser,
  Building2,
  Search,
} from "lucide-react";

import ColumnSearch from "../Common/ColumnSearch";
import { useVacancyByCampaignId } from "@/hooks/queries/usevacancy";
import VacancyForm from "../Vacancies/VacancyForm";
import { useCompanyVacancies } from "@/hooks/queries/usevacancy";
import Loader from "@/components/Loader";
import { useGetCampaignsList } from "@/hooks/queries/campaigns";
import { vacancyService } from "@/services/vacancyService";
import useToastStore from "@/components/Common/Toast/toastStore";
import Dropdown from "@/components/ui/Dropdown";
import Pagination from "../Common/Pagination";
import VacancyTable from "../campaign/VacancyTable";
import { mapVacancyData, useDebounce } from "@/utils/helpers";
import { PER_PAGE } from "@/config";
import usePaginationStore from "@/hooks/stores/usePaginationStore";

const Vacancies = ({ setActiveTab, campaignId }) => {
  // All hooks should be here, at the top level
  const { id: company_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToastStore;
  const { getPerPage, setPerPage } = usePaginationStore();
  const menuRef = useRef(null);
  const [statusMenu, setStatusMenu] = useState(null);
  const [filteredVacancies, setFilteredVacancies] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [columnFilters, setColumnFilters] = useState({
    title: "",
    department: "",
    location: "",
    status: "",
    applications: "",
    deadline: "",
  });

  const [isRefetching, setIsRefetching] = useState(false);

  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const [activeMenu, setActiveMenu] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(campaignId);
  const [serverPagination, setServerPagination] = useState({
    current_page: 1,
    per_page: getPerPage("vacancies-table"),
    total: 0,
    last_page: 50,
    next_page_url: null,
    prev_page_url: null,
  });

  // Data fetching hooks
  const {
    data: { vacancies, pagination } = { vacancies: [], pagination: {} },
    isLoading,
    isError,
    refetch: refetchVacancies,
  } = useCompanyVacancies(company_id, {
    search: searchTerm,
    per_page: serverPagination.per_page,
    page: serverPagination.current_page,
    status: [0, 1],
    campaign_id: selectedCampaign,
  });
  const {
    data: { campaigns } = { campaigns: [] },
    isLoading: isLoadingCampaigns,
  } = useGetCampaignsList(company_id);

  /*------------------------------------*/
  useEffect(() => {
    const fetchData = async () => {
      setIsRefetching(true);
      await refetchVacancies();
      setIsRefetching(false);
    };
    fetchData();
  }, [
    serverPagination.current_page,
    serverPagination.per_page,
    searchTerm,
    selectedCampaign,
  ]);

  const handlePageChange = (page) => {
    // Simply update the current page, fetchData will be triggered by the useEffect
    setServerPagination({ ...serverPagination, current_page: page });
    // console.log(serverPagination);
  };

  // Handle pagination dropdown change
  const handlePaginationChange = (selectedOption) => {
    const newPerPage = selectedOption.value;
    setPerPage("vacancies-table", newPerPage);
    setServerPagination((prev) => ({
      ...prev,
      per_page: newPerPage,
      current_page: 1, // Reset to first page when changing items per page
    }));
  };

  // function to handle search term input
  const updateSearchTerm = (term) => {
    setSearchTerm(term);
    setServerPagination({ ...serverPagination, current_page: 1 });
  };

  // defined useDebounce for search term input
  const debouncedSearch = useDebounce(updateSearchTerm, 1000);

  const handleSearchChange = (term) => {
    setTempSearchTerm(term);
    debouncedSearch(term);
  };

  /*------------------------------------*/

  // Effect hooks
  useEffect(() => {
    const processVacancies = async () => {
      setFilteredVacancies([]);
      if (vacancies?.length > 0) {
        const mappedVacancies = mapVacancyData({ vacancies });
        setFilteredVacancies(mappedVacancies);
      }
      if (pagination) {
        setServerPagination(pagination);
      }
    };
    processVacancies();
  }, [vacancies, pagination]);

  const handleCampaignChange = (selected) => {
    setSelectedCampaign(selected?.value || "");
    setServerPagination({ ...serverPagination, current_page: 1 });
  };

  const handleColumnSearch = (column, value) => {
    setColumnFilters((prev) => ({
      ...prev,
      [column]: value,
    }));
  };

  const addVacancy = (newVacancy) => {
    setFilteredVacancies((prevVacancies) => [...prevVacancies, newVacancy]);
    localStorage.setItem(
      "vacancies",
      JSON.stringify([...vacancies, newVacancy]),
    );
  };

  const handleDelete = (vacancyId) => {
    setFilteredVacancies((prevVacancies) =>
      prevVacancies.filter((vacancy) => vacancy.id !== vacancyId),
    );
  };

  const handleStatusChange = async (vacancyId, newStatus) => {
    try {
      // Call the API to update the status
      await vacancyService.updateVacancyStatus(vacancyId, {
        status: newStatus,
      });

      // Update local state with correct status mapping
      setFilteredVacancies((prevVacancies) =>
        prevVacancies
          .map((vacancy) =>
            vacancy.id === vacancyId
              ? {
                  ...vacancy,
                  status: newStatus,
                  // status: newStatus === 2 ? 'Archived' :
                  //         newStatus === 0 ? 'Draft' :
                  //         newStatus === 1 ? 'Published' :
                  //         vacancy.status // fallback to current status
                }
              : vacancy,
          )
          ?.filter((item) => item.status != 2),
      );

      // Show success message
      showToast({
        message: `Vacancy ${newStatus == 2 ? "archived" : newStatus == 0 ? "marked as Draft" : "published"} successfully`,
        type: "success",
      });

      // Close the menu
      setActiveMenu(null);
    } catch (error) {
      console.error("Error updating Vacancy status:", error);
      showToast({
        message: error.message || "Failed to update Vacancy status",
        type: "error",
      });
    }
  };

  const handleEdit = (id) => {
    navigate(`/vacancy/${id}`, { replace: true }); // Add replace: true to ensure proper navigation
    window.location.reload(); // Force a page refresh to ensure the page loads correctly
  };

  // Applicant click
  const handleApplicantsClick = (vacancy) => {
    setActiveTab("applications");
    const companyId = vacancy.campaign.company.id;
    navigate(`/companies/view/${companyId}`, {
      state: {
        activeTab: "applications",
        selectedVacancy: vacancy.id,
        fromApplicants: true,
      },
    });
  };

  // Apply padding p-6 only when navigating to '/vacancies'
  const containerClasses = `w-full ${location.pathname === "/vacancies" ? "p-6" : ""}`;

  return (
    <div className={containerClasses}>
      <div className="flex justify-between items-center mb-3">
        <h2
          className="text-xl font-semibold"
          style={{ alignSelf: "flex-start" }}
        >
          Vacancies
        </h2>
        <div className="flex flex-wrap items-center gap-4 justify-end">
          <div className="relative min-w-[200px] max-w-xs flex-1 sm:flex-initial">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              placeholder="Search vacancies..."
              value={tempSearchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full pl-10 pr-12 py-2 bg-card rounded-lg border border-input shadow-sm focus:ring-2 focus:ring-secondary/20 focus:border-secondary outline-none transition-all text-foreground placeholder:text-muted-foreground text-sm"
              aria-label="Search vacancies"
            />
          </div>
          <div className="relative">
            <Dropdown
              options={[
                ...campaigns?.map((campaign) => ({
                  label: campaign.title,
                  value: campaign.id,
                })),
              ]}
              value={
                selectedCampaign
                  ? {
                      label: campaigns?.find(
                        (campaign) => campaign.id === selectedCampaign,
                      )?.title,
                      value: selectedCampaign,
                    }
                  : null
              }
              onChange={handleCampaignChange}
              placeholder="All Campaigns"
              icon={<Building2 className="w-5 h-5 text-foreground flex-shrink-0" />}
              searchable={true}
              className="w-auto min-w-[200px]"
              dropdownWidth="w-auto min-w-[200px]"
              renderOption={(option) => (
                <p className="text-foreground">{option.label}</p>
              )}
              allowAll={true}
              allLabel="All Campaigns"
            />
          </div>
          <button
            onClick={() => navigate(`/campaigns/${company_id}/vacancies/add`)}
            className="bg-primary-dark text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors"
          >
            Add Vacancy
          </button>
        </div>
      </div>

      {/* Table */}
      {isError && <div>Error loading vacancies</div>}
      {isLoading || isRefetching ? (
        <Loader />
      ) : (
        <div className="bg-card rounded-lg overflow-y: auto;">
          <VacancyTable
            vacancies={filteredVacancies}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleStatusChange={handleStatusChange}
            onApplicantsClick={handleApplicantsClick}
            companyId={company_id}
            noVacancyMessage="No Vacancy Created yet"
            searchTerm={tempSearchTerm}
            handleSearchChange={handleSearchChange}
            serverPagination={serverPagination}
            onPageChange={handlePageChange}
            onPerPageChange={handlePaginationChange}
          />
        </div>
      )}
    </div>
  );
};

export default Vacancies;
